import { useLingui } from '@lingui/react/macro';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from '@mui/material';
import { type MouseEvent, memo, useCallback, useState } from 'react';
import { localeLabels, type SupportedLocale } from '../../locales/i18n.ts';
import { useLocaleSelection } from '../../locales/useLocaleSelection.ts';

const menuPaperSx = {
  minWidth: 200,
};

// The theme zeroes MuiMenu's list padding; the last option would otherwise sit flush on the edge.
const menuListSx = {
  pb: 1,
};

const subheaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  // MuiMenu strips the list's vertical padding, so the subheader carries its own.
  py: 1.5,
  lineHeight: 1.2,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontSize: '0.75rem',
  color: 'text.secondary',
  backgroundColor: 'transparent',
};

const checkIconSx = {
  minWidth: 0,
  ml: 2,
  color: 'primary.main',
};

/**
 * Header entry point: a globe icon that opens the language list in place. Deliberately its own
 * popover rather than a jump into the settings drawer — switching language is a one-tap task and
 * shouldn't cost the user a full-height drawer plus a hunt through the Interface section.
 */
export const LanguageMenuButton = memo(function LanguageMenuButton() {
  const { t } = useLingui();
  const { currentLocale, locales, selectLocale } = useLocaleSelection();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleToggle = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    (locale: SupportedLocale) => {
      selectLocale(locale);
      setAnchorEl(null);
    },
    [selectLocale]
  );

  const label = t({ comment: 'Accessible name of the header language picker', message: 'Language' });

  return (
    <>
      <IconButton onClick={handleToggle} aria-label={label} aria-haspopup="true" aria-expanded={open}>
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: menuPaperSx }, list: { sx: menuListSx } }}
      >
        {/* ListSubheader has no tabindex, so MenuList's keyboard navigation skips over it. */}
        <ListSubheader disableSticky sx={subheaderSx}>
          {label}
          <LanguageIcon fontSize="small" />
        </ListSubheader>
        {locales.map((locale) => (
          <MenuItem key={locale} selected={locale === currentLocale} onClick={() => handleSelect(locale)}>
            <ListItemText>{localeLabels[locale]}</ListItemText>
            {locale === currentLocale ? (
              <ListItemIcon sx={checkIconSx}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

// Aligns the option labels with the drawer's other row labels (56px icon slot + 16px row padding).
const nestedItemSx = {
  pl: 9,
};

/**
 * Mobile-drawer entry point: the same list, expanded inline. A popover anchored inside a drawer
 * is awkward on touch (two stacked dismiss layers), so the drawer keeps everything on one surface.
 */
export const LanguageDrawerSection = memo(function LanguageDrawerSection() {
  const { t } = useLingui();
  const { currentLocale, locales, selectLocale } = useLocaleSelection();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={handleToggle} aria-expanded={expanded}>
          <ListItemIcon>
            <LanguageIcon />
          </ListItemIcon>
          <ListItemText
            primary={t({ comment: 'Mobile drawer row opening the language list', message: 'Language' })}
            secondary={localeLabels[currentLocale]}
          />
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} unmountOnExit>
        <List disablePadding>
          {locales.map((locale) => (
            <ListItem key={locale} disablePadding>
              {/* The drawer stays open on purpose: the UI re-renders in the new language behind it. */}
              <ListItemButton sx={nestedItemSx} selected={locale === currentLocale} onClick={() => selectLocale(locale)}>
                <ListItemText primary={localeLabels[locale]} />
                {locale === currentLocale ? (
                  <ListItemIcon sx={checkIconSx}>
                    <CheckIcon fontSize="small" />
                  </ListItemIcon>
                ) : null}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
});
